// Pure helpers for the Merlin Watches page (tested in merlinWatchesUtils.test.ts).

import type { RunStatus, RunTrigger, Watch, WatchCadence, WatchRun } from '../services/merlinWatches';

export const CADENCE_OPTIONS: ReadonlyArray<{ value: WatchCadence; label: string; hint: string }> = [
  { value: 'daily', label: 'Daily', hint: 'A readout every day' },
  { value: 'every_48h', label: 'Every 48 hours', hint: 'Ravi’s cadence — a short cycle' },
  { value: 'weekly', label: 'Weekly', hint: 'A readout every week' },
  { value: 'monthly', label: 'Monthly', hint: 'A readout once a month' },
];

const TRIGGER_LABEL: Record<RunTrigger, string> = {
  created: 'first readout',
  scheduled: 'on schedule',
  manual: 'checked now',
  refinement: 'after a refinement',
};

const RUN_STATUS_LABEL: Record<RunStatus, string> = {
  queued: 'Queued',
  running: 'Merlin is checking…',
  published: 'Published',
  failed: 'Failed',
};

export function triggerLabel(trigger: RunTrigger): string {
  return TRIGGER_LABEL[trigger] ?? trigger;
}

export function runStatusLabel(status: RunStatus): string {
  return RUN_STATUS_LABEL[status] ?? status;
}

/** "3 days ago" / "in 2 days" / "just now" relative to `now` (ms). */
export function relativeTime(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const diff = t - now;
  const abs = Math.abs(diff);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  let body: string;
  if (abs < minute) body = 'just now';
  else if (abs < hour) body = `${Math.round(abs / minute)} min`;
  else if (abs < day) {
    const h = Math.round(abs / hour);
    body = `${h} hour${h === 1 ? '' : 's'}`;
  } else {
    const d = Math.round(abs / day);
    body = `${d} day${d === 1 ? '' : 's'}`;
  }
  if (body === 'just now') return body;
  return diff < 0 ? `${body} ago` : `in ${body}`;
}

/** The one-line status under a watch's name in the rail. */
export function watchStatusLine(w: Watch, now: number = Date.now()): string {
  if (w.status === 'removed') return 'Removed';
  if (w.open_run) return w.open_run.status === 'running' ? 'Merlin is checking now…' : 'Queued for Merlin';
  if (w.status === 'paused') return w.latest ? `Paused · last readout ${relativeTime(w.latest.created_at, now)}` : 'Paused';
  if (!w.latest) return 'No readout yet';
  const next = w.next_run_at ? ` · next ${relativeTime(w.next_run_at, now)}` : '';
  return `Updated ${relativeTime(w.latest.created_at, now)}${next}`;
}

/** The daily briefing first (it is the reader's standing tab), then watches
 * with Merlin at work, then by newest readout, then creation. */
export function sortWatches(items: Watch[]): Watch[] {
  const key = (w: Watch) => [
    w.kind === 'briefing' ? 0 : 1,
    w.open_run ? 0 : 1,
    -(w.latest ? Date.parse(w.latest.created_at) : 0),
    -Date.parse(w.created_at),
  ];
  return [...items].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    for (let i = 0; i < ka.length; i += 1) {
      if (ka[i] !== kb[i]) return ka[i] - kb[i];
    }
    return 0;
  });
}

/** The reader's live daily briefing, if they have one. */
export function findBriefing(items: Watch[]): Watch | null {
  return items.find((w) => w.kind === 'briefing' && w.status !== 'removed') ?? null;
}

/** True while any watch has Merlin at work — the page polls then. */
export function anyOpenRun(items: Watch[]): boolean {
  return items.some((w) => w.open_run !== null);
}

/** The failed run to explain, if the newest run failed and nothing newer published. */
export function lastFailure(runs: WatchRun[]): WatchRun | null {
  const newest = runs[0];
  return newest && newest.status === 'failed' ? newest : null;
}

/** The rail's name for a watch: a reader's own briefing reads as "My daily
 * briefing"; every other watch reads as its one-line headline (the subject's
 * first clause until a readout has named it, kanban f967fdaf: the subject
 * sentence read as a wall of text). */
export function railLabel(item: Watch, showOwner: boolean): string {
  return item.kind === 'briefing' && !showOwner ? 'My daily briefing' : item.headline;
}

/** The watches a daily briefing aggregates: its owner's other live plain
 * watches, in the order given (the "Across your watches" strip). */
export function acrossWatches(briefing: Watch, siblings: Watch[]): Watch[] {
  return siblings.filter(
    (s) =>
      s.id !== briefing.id &&
      s.kind !== 'briefing' &&
      s.status !== 'removed' &&
      s.owner_user_id === briefing.owner_user_id,
  );
}
